import { useState } from "react";
import { Link as NavLink } from "react-router";

import * as Api from "@/api";
import InputText from "@/design/forms/InputText";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import Valkyrie, { viPaperPlane, viSpinnerThird } from "@sippy-platform/valkyrie";

import { Button, Link, Stack, Typography } from "@mui/material";

import authReducer from "./authReducer";
import AuthUI from "./AuthUI";

export default function PasswordForgotten() {
  const [success, setSuccess] = useState(false);

  const { isValid, value, dispatch, errors, save, pending } = useSave({
    initialValue: { email: "" },
    mode: SaveMode.MANUAL,
    reducer: authReducer,
    validator: () => ({
      email: string()
        .required({ message: "Email address is required." })
        .email({ message: "The email address isn't valid." }),
    }),
    onSubmit: () => Api.postPasswordForgotten(value),
    onSuccess: () => setSuccess(true),
  });

  return (
    <AuthUI
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <title>{`Forgot password \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Typography variant="h4" sx={{ m: 0 }}>
        {success ? "Recovery email sent" : "Forgot password"}
      </Typography>
      {success ? (
        <Typography sx={{ mb: 2 }}>
          If {value.email} is linked to an existing account, you'll soon receive an email with a link to reset your
          password.
        </Typography>
      ) : (
        <Typography sx={{ mb: 2 }}>
          Forgot your password? No problem. Just let us know your email address and we will email you a password reset
          link that will allow you to choose a new one.
        </Typography>
      )}
      <InputText
        required
        placeholder="Email"
        id="email"
        type="email"
        label="Email"
        value={value.email}
        onChange={(id, value) => dispatch({ type: id, value })}
        error={errors}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Link component={NavLink} to="/login" color="inherit" underline="hover" variant="body2">
          Login
        </Link>
      </Stack>
      <Button
        variant="contained"
        size="large"
        id="submit"
        type="submit"
        autoFocus
        endIcon={<Valkyrie icon={pending ? viSpinnerThird : viPaperPlane} spin={pending} />}
        disabled={pending || !isValid}
      >
        {success ? "Send again" : "Send recovery email"}
      </Button>
    </AuthUI>
  );
}
